import React, { useState } from "react";
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Alert,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { googleSsoLogin } from "../../Action/api.js";
import OrunalLogo from "../../Components/Reusable/OrunalLogo";
import { GoogleLogin } from "@react-oauth/google";

export const Login = () => {
  const [loading, setLoading] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const navigate = useNavigate();

  // Role-to-Route Mapping
  const roleRoutes = {
    "AUJ-GROUP-001": "/founder-dashboard", // Chief Product Officer
    "U-PT-ROLE-001": "/cpo-dashboard", // Chief Product Officer
    "U-PT-ROLE-002": "/product-manager-dashboard", // Product Manager
    "U-PT-ROLE-003": "/associate-product-manager-dashboard", // Associate Product Manager
    "U-PT-ROLE-004": "/junior-product-manager-dashboard", // Junior Product Manager
    "U-PT-ROLE-005": "/product-intern-dashboard", // Product Intern / Trainee
    "U-MG-ROLE-001": "/cmo-dashboard", // Chief Marketing Officer
    "U-MG-ROLE-002": "/growth-marketing-dashboard", // Growth Marketing Manager
    "U-MG-ROLE-003": "/content-strategist-dashboard", // Content Strategist
    // "U-MG-ROLE-004": "/social-media-dashboard", // Social Media Executive
    "U-SB-ROLE-001": "/vp-sales-dashboard", // VP of Sales
    "U-SB-ROLE-002": "/sales-manager-dashboard", // Sales Manager
    "U-SB-ROLE-003": "/business-development-dashboard", // Business Development Executive
    // "U-SB-ROLE-004": "/executive-trainee-dashboard", // Executive Trainee
    "U-HR-ROLE-001": "/executive-hr-dashboard", // Executive Trainee
    "U-HR-ROLE-002": "/senior-trainee-dashboard", // Executive Trainee
    "U-HR-ROLE-003": "/mid-senior-trainee-dashboard", // Executive Trainee
    "U-HR-ROLE-004": "/junior-trainee-dashboard", // Executive Trainee
  };

  const handleGoogle = async (credentialResponse) => {
    setErrorMessage("");

    try {
      const googleCredential = credentialResponse?.credential;
      if (!googleCredential) {
        setErrorMessage("Google authentication failed. Missing credential token.");
        return;
      }

      const response = await googleSsoLogin(googleCredential);

      if (!response.data || response.isAxiosError || !response.data.status) {
        setErrorMessage(
          response.data?.uiMsg || "Google login failed. Please try again."
        );
        setLoading(false);
        return;
      }

      const responseData = response.data;
      const userData = responseData.userData;

      if (userData) {
        // Check if user is not activated (staffStatus is Unknown)
        if (userData.staffStatus === "Unknown") {
          setErrorMessage("User ID is unauthorized. Please check with the Orunal team to get activated.");
          return;
        }

        const roleId = userData.roleId || "";
        const appAccess = userData.appAccess || [];

        // Store authorization token
        localStorage.setItem("authToken", responseData.authorizationToken || "");
        localStorage.setItem("isAuthenticated", "true");
        localStorage.setItem("loginTime", Date.now());

        // Find main app and redirect
        let mainAppUrl = null;
        for (const appItem of appAccess) {
          const appKey = Object.keys(appItem)[0];
          const appData = appItem[appKey];
          if (appData.mainApp === true && appData.permission === true && appData.url) {
            mainAppUrl = appData.url;
            break;
          }
        }

        if (mainAppUrl) {
          setRedirecting(true);
          const urlObj = new URL(mainAppUrl);
          urlObj.searchParams.set('token', responseData.authorizationToken);
          setTimeout(() => {
            window.location.href = urlObj.toString();
          }, 800);
        } else {
          const route = roleRoutes[roleId];
          if (route) {
            navigate(route);
          } else {
            navigate("/task-dashboard");
          }
        }
      } else {
        setErrorMessage("Google authentication successful but account not found!");
      }
    } catch (error) {
      console.error("Google login error:", error);
      setErrorMessage("An error occurred during Google sign-in. Please try again.");
    }

    setLoading(false);
  };

  const handleGoogleError = () => {
    setErrorMessage("Google sign-in failed or was cancelled. Please try again.");
  };

  return (
    <Box
      sx={{
        width: "100%",
      }}
    >
      {errorMessage && (
        <Alert severity="error" sx={{ marginBottom: 3, borderRadius: 2 }}>
          {errorMessage}
        </Alert>
      )}

      {/* Welcome Text */}
      <Typography
        sx={{
          textAlign: "center",
          color: "#344054",
          fontSize: { xs: "0.95rem", sm: "1rem" },
          marginBottom: { xs: 2.5, sm: 3 },
          lineHeight: 1.6,
        }}
      >
        Sign in securely with your Google account to access your workspace
      </Typography>

      {/* Google Login Button */}
      <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
        {loading ? (
          <Box
            sx={{
              width: '100%',
              padding: { xs: "14px", sm: "15px" },
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              borderRadius: "8px",
              border: "1.5px solid #e4e7eb",
              backgroundColor: "#f9fafb",
            }}
          >
            <CircularProgress size={22} sx={{ color: "#667085" }} />
          </Box>
        ) : (
          <GoogleLogin
            onSuccess={handleGoogle}
            onError={handleGoogleError}
            theme="outline"
            size="large"
            width="100%"
            text="continue_with"
            shape="rectangular"
          />
        )}
      </Box>

      {/* Security Notice */}
      <Box
        sx={{
          marginTop: { xs: 3, sm: 3.5 },
          padding: { xs: 2, sm: 2.5 },
          backgroundColor: "#f0f9ff",
          borderRadius: "8px",
          border: "1px solid #bfdbfe",
        }}
      >
        <Typography
          sx={{
            fontSize: { xs: "0.8rem", sm: "0.85rem" },
            color: "#1e40af",
            textAlign: "center",
            lineHeight: 1.6,
          }}
        >
          🔒 Your data is protected with enterprise-grade security. We never store your Google password.
        </Typography>
      </Box>

      {/* Redirecting Loader Overlay */}
      {redirecting && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(255, 255, 255, 0.98)",
            backdropFilter: "blur(8px)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            animation: "fadeIn 0.3s ease-in",
          }}
        >
          <Box
            sx={{
              textAlign: "center",
              maxWidth: 400,
              padding: 4,
            }}
          >
            {/* Animated Logo or Icon */}
            <Box
              sx={{
                width: "100%",
                maxWidth: 200,
                margin: "0 auto 24px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                animation: "pulse 1.5s ease-in-out infinite",
              }}
            >
              <OrunalLogo 
                fontSize={{ xs: "48px", sm: "56px", md: "64px" }}
                showUnderline={false}
                spacing={{ xs: 0, sm: 0 }}
              />
            </Box>

            {/* Loading Spinner */}
            <CircularProgress
              size={48}
              thickness={4}
              sx={{
                color: "#0176d3",
                marginBottom: 3,
              }}
            />

            {/* Loading Text */}
            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                color: "#1a1a1a",
                marginBottom: 1,
              }}
            >
              Taking you to your workspace...
            </Typography>
            <Typography
              sx={{
                color: "#667085",
                fontSize: "0.95rem",
              }}
            >
              Please wait while we securely connect you
            </Typography>
          </Box>

          {/* Add keyframe animations */}
          <style>
            {`
              @keyframes fadeIn {
                from { opacity: 0; }
                to { opacity: 1; }
              }
              @keyframes pulse {
                0%, 100% { transform: scale(1); opacity: 1; }
                50% { transform: scale(1.05); opacity: 0.9; }
              }
            `}
          </style>
        </Box>
      )}
      </Box>
  );
};

export default Login;
